/**
 * The audit log: voids, money off, refunds, drawer variances, price changes,
 * waste, counts, and who can do what — newest first, as far back as the plan
 * keeps.
 *
 * Entries are written by the server in the same transaction as the action, so
 * there is nothing here to write: the log is read-only from every screen.
 */

import { useInfiniteQuery } from '@tanstack/react-query';

import { useApi } from './provider';

export type AuditCategory = 'sales' | 'money' | 'menu' | 'stock' | 'people' | 'kitchen';

export interface AuditEntry {
  id: string;
  occurred_at: string;
  category: AuditCategory;
  event: string;
  summary: string;
  actor_id: string | null;
  /** Null when nobody did it: work that followed on from another action. */
  actor_name: string | null;
  location_id: string | null;
}

export interface AuditPage {
  entries: AuditEntry[];
  /** Pass back as `before` for the next page. Null on the last. */
  next_before: string | null;
  /** How many days back the log reads on this plan. Null: everything. */
  keep_days: number | null;
}

export interface AuditFilter {
  category?: AuditCategory | null;
  actorId?: string | null;
  locationId?: string | null;
}

export function useAuditLog(filter: AuditFilter = {}) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [
      'audit',
      filter.category ?? null,
      filter.actorId ?? null,
      filter.locationId ?? null,
    ],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams();
      if (filter.category) params.set('category', filter.category);
      if (filter.actorId) params.set('actor_id', filter.actorId);
      if (filter.locationId) params.set('location_id', filter.locationId);
      if (pageParam) params.set('before', pageParam);
      const query = params.toString();
      return api.get<AuditPage>(`/v1/audit${query ? `?${query}` : ''}`, signal);
    },
    getNextPageParam: (last) => last.next_before,
  });
}
